import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/toast';
import { IProductCategory } from '../../shared/interfaces';
import api from '../../services/api';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import {
  AppstoreAddOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import ProductCategoryCreateEdit from './CreateEdit';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const ProductsCategories: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [categories, setCategories] = useState<IProductCategory[]>([]);
  const [category, setCategory] = useState<IProductCategory | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listCategories = useCallback(async () => {
    setLoading(true);
    setCategories([]);
    try {
      const response = await api.post('/products-categories/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setCategories(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar categorias',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalProductCategoryCreateEdit = () => {
    if (modalCreate)
      return (
        <ProductCategoryCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          category={category}
          setCategories={setCategories}
        />
      );
  };

  useEffect(() => {
    listCategories();
  }, [page, search, limit, listCategories]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<AppstoreAddOutlined />}
        title="Categoria de produtos"
        description="Organize as categorias do estoque e mantenha a estrutura do catálogo alinhada com o restante do sistema."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listCategories(),
          },
          permissionFind([PermissionActions.productsCategoriesCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setCategory(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IProductCategory) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.productsCategoriesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setCategory(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={categories}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalProductCategoryCreateEdit()}
    </PageLayout>
  );
};

export default ProductsCategories;
